/**
 * Player settings. Persisted to localStorage, applied live where the engine allows it
 * (sensitivity, FOV, camera) and on the next reload where it does not (quality tier,
 * which the renderer resolves once at boot).
 */

const KEY = 'stillsun.settings';

export type QualityChoice = 'auto' | 'low' | 'high' | 'ultra';
/**
 * Who set `quality`. The renderer's governor writes 'governor' with its step-down; the Settings
 * tab writes 'player'. A stored non-auto tier with neither is a leftover from before this field
 * existed — including the governor latching Low on CPU-bound frames it could not fix
 * (2026-09-05) — and is dropped back to auto once.
 */
export type QualityBy = 'none' | 'player' | 'governor';

export interface Settings {
  /** Multiplier on every mouse look delta. */
  sensitivity: number;
  /** World vertical FOV in degrees. The camera owns it outright now (plan §1c). */
  fov: number;
  quality: QualityChoice;
  qualityBy: QualityBy;
  /** Flips the vertical axis while orbiting the camera with the right button. */
  invertPitch: boolean;
  /** Attach a screenshot by default in the feedback form. */
  screenshotDefault: boolean;
  /** Overall audio level, 0..1 — see `SoundManager.setMasterVolume`. */
  masterVolume: number;
  /** Weapons/footsteps/vox/charge bus, 0..1. */
  sfxVolume: number;
  /** HUD feedback bus (hit marker, loot pickup, ticks/confirms), 0..1. */
  uiVolume: number;
  /** Ambience bed bus (Sexton drone, beacon hum, Dimming wind), 0..1. */
  ambienceVolume: number;
  /** Silences all audio without touching the individual bus levels. */
  muted: boolean;
  /** Rendering kill switches, mirrored by the URL flags of the same name (`?taa=0` etc.);
   * either source off = off. Read once at boot by render/renderer.ts, so they apply on reload. */
  taa: boolean;
  csm: boolean;
  gtao: boolean;
  noise: boolean;
  bloom: boolean;
}

export const DEFAULT_SETTINGS: Settings = {
  sensitivity: 1,
  fov: 70,
  quality: 'auto',
  qualityBy: 'none',
  invertPitch: false,
  screenshotDefault: true,
  masterVolume: 0.8,
  sfxVolume: 1,
  uiVolume: 1,
  ambienceVolume: 1,
  muted: false,
  taa: true,
  csm: true,
  gtao: true,
  noise: true,
  bloom: true,
};

export const SETTINGS_LIMITS = {
  sensitivity: [0.2, 3] as const,
  fov: [60, 110] as const,
  masterVolume: [0, 1] as const,
  sfxVolume: [0, 1] as const,
  uiVolume: [0, 1] as const,
  ambienceVolume: [0, 1] as const,
};

const clampTo = (v: number, range: readonly [number, number]): number =>
  Math.min(range[1], Math.max(range[0], v));

function sanitise(raw: unknown): Settings {
  const out = { ...DEFAULT_SETTINGS };
  if (raw === null || typeof raw !== 'object') return out;
  const rec = raw as Record<string, unknown>;
  if (typeof rec.sensitivity === 'number' && Number.isFinite(rec.sensitivity)) {
    out.sensitivity = clampTo(rec.sensitivity, SETTINGS_LIMITS.sensitivity);
  }
  if (typeof rec.fov === 'number' && Number.isFinite(rec.fov)) {
    out.fov = clampTo(rec.fov, SETTINGS_LIMITS.fov);
  }
  if (
    rec.quality === 'auto' ||
    rec.quality === 'low' ||
    rec.quality === 'high' ||
    rec.quality === 'ultra'
  ) {
    out.quality = rec.quality;
  }
  if (rec.qualityBy === 'player' || rec.qualityBy === 'governor') out.qualityBy = rec.qualityBy;
  if (out.quality !== 'auto' && out.qualityBy === 'none') out.quality = 'auto';
  if (typeof rec.invertPitch === 'boolean') out.invertPitch = rec.invertPitch;
  if (typeof rec.screenshotDefault === 'boolean') out.screenshotDefault = rec.screenshotDefault;
  if (typeof rec.masterVolume === 'number' && Number.isFinite(rec.masterVolume)) {
    out.masterVolume = clampTo(rec.masterVolume, SETTINGS_LIMITS.masterVolume);
  }
  if (typeof rec.sfxVolume === 'number' && Number.isFinite(rec.sfxVolume)) {
    out.sfxVolume = clampTo(rec.sfxVolume, SETTINGS_LIMITS.sfxVolume);
  }
  if (typeof rec.uiVolume === 'number' && Number.isFinite(rec.uiVolume)) {
    out.uiVolume = clampTo(rec.uiVolume, SETTINGS_LIMITS.uiVolume);
  }
  if (typeof rec.ambienceVolume === 'number' && Number.isFinite(rec.ambienceVolume)) {
    out.ambienceVolume = clampTo(rec.ambienceVolume, SETTINGS_LIMITS.ambienceVolume);
  }
  if (typeof rec.muted === 'boolean') out.muted = rec.muted;
  for (const k of ['taa', 'csm', 'gtao', 'noise', 'bloom'] as const) {
    if (typeof rec[k] === 'boolean') out[k] = rec[k];
  }
  return out;
}

/** Read without constructing the store — the renderer needs the quality tier at boot. */
export function loadSettings(): Settings {
  try {
    const text = localStorage.getItem(KEY);
    if (text === null) return { ...DEFAULT_SETTINGS };
    return sanitise(JSON.parse(text));
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

type Listener = (s: Settings) => void;

export class SettingsStore {
  private state: Settings = loadSettings();
  private readonly listeners = new Set<Listener>();

  get current(): Settings {
    return this.state;
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.state);
    return () => {
      this.listeners.delete(fn);
    };
  }

  set<K extends keyof Settings>(key: K, value: Settings[K]): void {
    const next = sanitise({
      ...this.state,
      [key]: value,
      ...(key === 'quality' ? { qualityBy: 'player' } : {}),
    });
    this.state = next;
    try {
      localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      /* private mode: settings stay for this session only */
    }
    for (const fn of [...this.listeners]) {
      try {
        fn(next);
      } catch (err) {
        console.error('[stillsun] settings listener threw', err);
      }
    }
  }

  reset(): void {
    for (const key of Object.keys(DEFAULT_SETTINGS) as (keyof Settings)[]) {
      this.set(key, DEFAULT_SETTINGS[key]);
    }
  }
}

export const settings = new SettingsStore();
